import classnames from 'classnames/bind';
import React from 'react';
import styles from './HeaderTitle.scss';
import { useHeaderState } from '../context/HeaderContext';

export interface HeaderTitleProps {
    title: string | JSX.Element;
    titleStyles?: string;
    titleAriaLabel?: string;
    titleRef?: React.RefObject<HTMLDivElement>;
    subTitle?: string | JSX.Element;
    subTitleStyles?: string;
    subTitleAriaLabel?: string;
    subTitleRef?: React.RefObject<HTMLDivElement>;
    ariaHidden?: boolean;
    className?: string;
}
export type HeaderTitleAlign = 'left' | 'right' | 'center';

const cx = classnames.bind(styles);

/**
 * Component to render Header title.
 * Focus needs to be set explicitly by the parent. The focus setting logic, earlier present in the older Header component,
 * created problems with multiple controls calling their own trySetFocus.
 */
export const HeaderTitle = React.forwardRef(
    (props: HeaderTitleProps, ref?: React.RefObject<HTMLDivElement>) => {
        const {
            title,
            titleStyles,
            titleRef = ref,
            subTitle,
            subTitleStyles,
            subTitleAriaLabel,
            subTitleRef,
            ariaHidden = false,
            className,
        } = props;
        const { titleAlign } = useHeaderState();

        const titleAriaProps =
            title && !ariaHidden
                ? {
                      role: 'heading',
                      'aria-level': 1,
                      tabIndex: 0,
                  }
                : {};

        const subTitleAriaProps =
            subTitle && !ariaHidden
                ? {
                      'aria-label': getTitleAriaLabel(subTitle, subTitleAriaLabel),
                      tabIndex: 0,
                  }
                : {};

        const titleAlignStyles = getAlignmentStyles(titleAlign);

        return title ? (
            <div className={cx(styles.titleContainer, titleAlignStyles, className)}>
                <div ref={titleRef} className={cx(styles.title, titleStyles)} {...titleAriaProps}>
                    {title}
                </div>
                {subTitle && (
                    <div
                        ref={subTitleRef}
                        {...subTitleAriaProps}
                        className={cx(styles.subTitle, subTitleStyles)}
                        aria-label={getTitleAriaLabel(subTitle, subTitleAriaLabel)}>
                        {subTitle}
                    </div>
                )}
            </div>
        ) : null;
    }
);

const getAlignmentStyles = (titleAlign: HeaderTitleAlign) => {
    switch (titleAlign) {
        case 'left':
            return styles.titleLeft;
        case 'right':
            return styles.titleRight;
        case 'center':
        default:
            return styles.titleCenter;
    }
};

const getTitleAriaLabel = (title: string | JSX.Element, ariaLabelProp: string): string =>
    ariaLabelProp ? ariaLabelProp : typeof title === 'string' ? title : undefined;
